import { createAuthQuery, createLoginQuery } from "@mozioinc/query";
import { useSessionStore } from "../stores/session-store";
import { getEnvs } from "./env";
import { resetPostHogUser } from "./posthog";
import { stashExpiredSessionUserId } from "./session-identity";
import { getAccessToken, removeTokens } from "./token";
import { getWhiteLabel } from "./white-label";

export const redirectToLogin = () => {
  const encodedPathWithSearch = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  window.location.href = `/user/login?returnPath=${encodedPathWithSearch}`;
};

export const handleAuthCheck = () => {
  if (!getAccessToken() && getWhiteLabel().authRequired) {
    removeTokens();
    redirectToLogin();
  }
};

const { QUERY_ENV } = getEnvs();

export const authQuery = createAuthQuery(() => ({
  ctx: {
    env: QUERY_ENV,
  },
}));

export const loginQuery = createLoginQuery(() => ({
  ctx: {
    env: QUERY_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
  },
}));

// Global singleton to prevent duplicate refresh token calls
let activeRefreshPromise:
  | ReturnType<typeof authQuery.refreshTokens>
  | undefined;

export const refreshTokenSingleton = async (refreshToken: string) => {
  if (activeRefreshPromise) {
    return activeRefreshPromise;
  }

  activeRefreshPromise = authQuery.refreshTokens(refreshToken);

  try {
    const result = await activeRefreshPromise;
    return result;
  } catch (error) {
    console.error("Refresh token failed:", error);
    throw error;
  } finally {
    activeRefreshPromise = undefined;
  }
};

/**
 * Thrown by the API client when a 401 cannot be recovered from — the refresh
 * token is missing, expired, or the retry budget is spent.
 *
 * It carries no data: its only job is to be recognizable. `useGenericErrorToast`
 * checks for it and stays silent, because `SessionExpiredDialog` owns the
 * user-facing recovery path for this case (DEV-12536).
 */
export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

/**
 * The session is gone and cannot be recovered. Records who it belonged to (for
 * travel-agent attribution), drops the dead tokens, and raises the dialog.
 *
 * Returns the error to throw rather than throwing itself, so call sites read as
 * `throw expireSession()`.
 *
 * Shared by both API clients in this app — api-client.ts (openapi-fetch) and
 * query.ts (@mozioinc/query fetchers). Both must end up in the same place, or
 * one client's hard redirect navigates away from the other's dialog.
 */
export const expireSession = () => {
  // Order matters: this reads the access token that removeTokens() discards.
  stashExpiredSessionUserId();
  removeTokens();
  resetPostHogUser();
  useSessionStore.getState().markExpired();
  return new SessionExpiredError();
};
